import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletAPI } from '../services/api';
import './Exchange.css';

function Exchange() {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exchangeRates] = useState({
    'EUR-GBP': 0.85,
    'GBP-EUR': 1.18,
    'EUR-USD': 1.10,
    'USD-EUR': 0.91,
    'GBP-USD': 1.27,
    'USD-GBP': 0.79
  });
  
  const [formData, setFormData] = useState({
    from_wallet_id: '',
    to_wallet_id: '',
    from_amount: '',
    to_amount: '',
    exchange_rate: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadWallets();
  }, []);

  const loadWallets = async () => {
    try {
      const response = await walletAPI.list();
      setWallets(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading pots:', err);
      setLoading(false);
    }
  };

  const getExchangeRate = (fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return 1;
    const key = `${fromCurrency}-${toCurrency}`;
    return exchangeRates[key] || 1;
  };

  const handleFromWalletChange = (e) => {
    const walletId = parseInt(e.target.value);
    const fromWallet = wallets.find(w => w.id === walletId);
    const toWallet = wallets.find(w => w.id === formData.to_wallet_id);
    
    setFormData(prev => ({ ...prev, from_wallet_id: walletId }));
    
    if (fromWallet && toWallet) {
      const rate = getExchangeRate(fromWallet.currency, toWallet.currency);
      setFormData(prev => ({
        ...prev,
        exchange_rate: rate.toFixed(4),
        to_amount: prev.from_amount ? (parseFloat(prev.from_amount) * rate).toFixed(2) : ''
      }));
    }
  };

  const handleToWalletChange = (e) => {
    const walletId = parseInt(e.target.value);
    const fromWallet = wallets.find(w => w.id === formData.from_wallet_id);
    const toWallet = wallets.find(w => w.id === walletId);
    
    setFormData(prev => ({ ...prev, to_wallet_id: walletId }));
    
    if (fromWallet && toWallet) {
      const rate = getExchangeRate(fromWallet.currency, toWallet.currency);
      setFormData(prev => ({
        ...prev,
        exchange_rate: rate.toFixed(4),
        to_amount: prev.from_amount ? (parseFloat(prev.from_amount) * rate).toFixed(2) : ''
      }));
    }
  };

  const handleFromAmountChange = (e) => {
    const amount = e.target.value;
    setFormData(prev => ({ 
      ...prev, 
      from_amount: amount,
      to_amount: amount && prev.exchange_rate ? (parseFloat(amount) * parseFloat(prev.exchange_rate)).toFixed(2) : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.from_wallet_id === formData.to_wallet_id) {
      alert('Cannot transfer between the same pot!');
      return;
    }
    
    try {
      await walletAPI.exchange({
        from_wallet_id: parseInt(formData.from_wallet_id),
        to_wallet_id: parseInt(formData.to_wallet_id),
        from_amount: parseFloat(formData.from_amount),
        to_amount: parseFloat(formData.to_amount),
        exchange_rate: parseFloat(formData.exchange_rate),
        date: formData.date,
        notes: formData.notes
      });
      
      alert('✓ Transfer completed successfully!');
      navigate('/wallets');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF', 'JPY': '¥' };
    return symbols[currency] || currency;
  };

  const fromWallet = wallets.find(w => w.id === formData.from_wallet_id);
  const toWallet = wallets.find(w => w.id === formData.to_wallet_id);

  if (loading) return <div className="exchange-loading">Loading...</div>;

  return (
    <div className="exchange-container">
      <div className="exchange-header">
        <button className="back-btn" onClick={() => navigate('/wallets')}>← Back to Pots</button>
        <h1>💱 Transfer Between Pots</h1>
      </div>

      <form onSubmit={handleSubmit} className="exchange-form">
        <div className="wallet-section">
          <h3>From</h3>
          <select value={formData.from_wallet_id} onChange={handleFromWalletChange} required>
            <option value="">Select pot...</option>
            {wallets.map(w => (
              <option key={w.id} value={w.id}>
                {w.icon || '🫙'} {w.name} ({getCurrencySymbol(w.currency)}{w.balance.toFixed(2)})
              </option>
            ))}
          </select>
          <input
            type="number"
            step="0.01"
            value={formData.from_amount}
            onChange={handleFromAmountChange}
            placeholder="Amount"
            required
          />
          {fromWallet && <div className="currency">{getCurrencySymbol(fromWallet.currency)}</div>}
        </div>

        <div className="exchange-rate">
          <div className="rate-icon">⇅</div>
          <label>Rate: 1 {fromWallet?.currency} = {formData.exchange_rate || '?'} {toWallet?.currency}</label>
          <input
            type="number"
            step="0.0001"
            value={formData.exchange_rate}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              exchange_rate: e.target.value,
              to_amount: prev.from_amount ? (parseFloat(prev.from_amount) * parseFloat(e.target.value)).toFixed(2) : ''
            }))}
            placeholder="1.0000"
            required
          />
        </div>

        <div className="wallet-section">
          <h3>To</h3>
          <select value={formData.to_wallet_id} onChange={handleToWalletChange} required>
            <option value="">Select pot...</option>
            {wallets.filter(w => w.id !== formData.from_wallet_id).map(w => (
              <option key={w.id} value={w.id}>
                {w.icon || '🫙'} {w.name} ({getCurrencySymbol(w.currency)}{w.balance.toFixed(2)})
              </option>
            ))}
          </select>
          <input
            type="number"
            step="0.01"
            value={formData.to_amount}
            readOnly
            placeholder="Amount"
          />
          {toWallet && <div className="currency">{getCurrencySymbol(toWallet.currency)}</div>}
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            required
          />
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Optional notes..."
          />
        </div>

        <button type="submit" className="submit-btn">Complete Transfer</button>
      </form>
    </div>
  );
}

export default Exchange;
